import { isNullish, objectFilter, nullishFilter } from '@caresend/utils';
import { ref, reactive, isRef, watch } from 'vue';
import '@caresend/types';
import 'file-saver';
import '../../../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../../../database/encryption/model.js';
import { generateHash } from '../../../../../../database/encryption/utils/helpers.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';

/**
 * This is a persistence system to support vue reactive elements and syncronize them with local storage
 *
 * @pumposh
 */
/**
 * Generate a unique key for a given value
 *
 * The initial value is used here to ensure if the expectation for the
 * data type is different than the actual data type stored in local storage,
 * the hash will be different and thus hydration would fail.
 * @param key - The key to use for local storage
 * @param initialValue - The initial value to use for hashing
 */
function getKey(key, initialValue) {
    const hash = generateHash({ key, initialValue });
    return `persist--${key}--${hash}`;
}
/** Used to convert Map and Set objects to a format that can be stored in local storage */
function replacer(_key, value) {
    if (value instanceof Map) {
        return {
            dataType: 'Map',
            value: Array.from(value.entries()),
        };
    }
    if (value instanceof Set) {
        return {
            dataType: 'Set',
            value: Array.from(value),
        };
    }
    return value;
}
/** Used to convert Map and Set objects to a format that can be stored in local storage */
function reviver(_key, value) {
    if (typeof value === 'object' && value !== null) {
        if (value.dataType === 'Map') {
            return new Map(value.value);
        }
        if (value.dataType === 'Set') {
            return new Set(value.value);
        }
    }
    return value;
}
// TODO: Add better error handling for unexpected failed parse. See CARE-4826
// in which the `forceParse` function used by encryption was revised. Replace
// duplicate declarations of this function in dashboard.
const forceParse = (data) => {
    try {
        return JSON.parse(data, reviver);
    }
    catch {
        return data;
    }
};
/**
 * Used to initialize a vue reactive element and synchronize any changes with local storage
 * @param key - The key to use for local storage
 * @param value - The vue reactive element to watch
 * @param includeNullish - Whether to include nullish values in the local storage
 */
const initWatch = (key, value, includeNullish) => {
    const predicate = isRef(value)
        ? value
        : () => value;
    watch(predicate, (newValue) => {
        if (isRef(value)) {
            if (Array.isArray(newValue) || typeof newValue === 'object') {
                localStorage.setItem(key, JSON.stringify(newValue, replacer));
            }
            else if (!isNullish(newValue)) {
                localStorage.setItem(key, `${newValue}`);
            }
        }
        else {
            const valueToStore = includeNullish
                ? newValue
                : objectFilter(newValue !== null && newValue !== void 0 ? newValue : {}, nullishFilter);
            if (Object.keys(valueToStore).length > 0) {
                localStorage.setItem(key, JSON.stringify(valueToStore, replacer));
            }
            else {
                localStorage.removeItem(key);
            }
        }
    }, {
        deep: true,
        immediate: true,
    });
};
const ls = {
    ref(key, defaultValue) {
        const cachedValue = localStorage.getItem(getKey(key, defaultValue));
        const parsedValue = cachedValue
            ? forceParse(cachedValue)
            : defaultValue;
        const value = ref(parsedValue !== null && parsedValue !== void 0 ? parsedValue : defaultValue);
        initWatch(getKey(key, defaultValue), value);
        return value;
    },
    reactive(key, defaultValue, includeNullish) {
        const cachedValue = localStorage.getItem(getKey(key, defaultValue));
        const parsedValue = cachedValue
            ? forceParse(cachedValue)
            : defaultValue;
        const value = reactive(parsedValue);
        initWatch(getKey(key, defaultValue), value, includeNullish);
        return value;
    },
};

export { forceParse, ls };
